import { useEffect, useState } from 'react';
import { useSession } from './session-context';
import {
  WorkspaceContext,
  type Organization,
  type UserWorkspace,
} from './workspace-context';
import { Sentry } from '../lib/sentry';
import { WORKSPACE_STORAGE_KEY } from '../constants/workspace';
import { apiFetch } from '../lib/api';
import { ENDPOINTS } from '../constants/endpoints';

type Props = { children: React.ReactNode };

export const WorkspaceProvider = ({ children }: Props) => {
  const { session } = useSession();
  const [currentWorkspace, setCurrentWorkspace] = useState<Organization | null>(
    null,
  );
  const [availableWorkspaces, setAvailableWorkspaces] = useState<
    Organization[]
  >([]);
  const [isLoadingWorkspaces, setIsLoadingWorkspaces] = useState(true);
  const [canSwitchWorkspaces, setCanSwitchWorkspaces] = useState(false);

  useEffect(() => {
    if (!session?.user) {
      setIsLoadingWorkspaces(false);
      return;
    }

    loadWorkspaces()
      .then(({ workspaces, startingWorkspace }) => {
        setAvailableWorkspaces(workspaces);
        setCanSwitchWorkspaces(workspaces.length > 1);
        if (!startingWorkspace) return;
        setCurrentWorkspace(startingWorkspace);
        rememberWorkspace(startingWorkspace);
      })
      .catch((error) => Sentry.captureException(error))
      .finally(() => setIsLoadingWorkspaces(false));
  }, [session]);

  const switchWorkspace = (orgId: number) => {
    const pickedWorkspace = availableWorkspaces.find(
      (workspace) => workspace.id === orgId,
    );
    if (!pickedWorkspace) return;
    setCurrentWorkspace(pickedWorkspace);
    rememberWorkspace(pickedWorkspace);
  };

  return (
    <WorkspaceContext.Provider
      value={{
        currentWorkspace,
        availableWorkspaces,
        switchWorkspace,
        isLoadingWorkspaces,
        canSwitchWorkspaces,
      }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
};

async function loadWorkspaces() {
  const response = await apiFetch(ENDPOINTS.WORKSPACE_LIST);
  if (!response.ok) throw new Error('Failed to fetch workspaces');
  const { workspaces: userWorkspaces } = (await response.json()) as {
    workspaces: UserWorkspace[];
  };
  const workspaces = userWorkspaces.map(toOrganization);
  const startingWorkspace = pickStartingWorkspace(
    workspaces,
    findDefaultWorkspace(userWorkspaces),
  );
  return { workspaces, startingWorkspace };
}

function toOrganization(userWorkspace: UserWorkspace): Organization {
  return {
    id: userWorkspace.id,
    name: userWorkspace.name,
    tier: userWorkspace.tier,
  };
}

function findDefaultWorkspace(
  userWorkspaces: UserWorkspace[],
): Organization | null {
  const userDefault = userWorkspaces.find((workspace) => workspace.isDefault);
  return userDefault ? toOrganization(userDefault) : null;
}

function pickStartingWorkspace(
  workspaces: Organization[],
  defaultWorkspace: Organization | null,
): Organization | null {
  if (workspaces.length === 0) return null;
  if (workspaces.length > 1) {
    const savedWorkspace = findSavedWorkspace(workspaces);
    if (savedWorkspace) return savedWorkspace;
  }
  return defaultWorkspace ?? workspaces[0];
}

function findSavedWorkspace(workspaces: Organization[]): Organization | null {
  const savedWorkspaceId = localStorage.getItem(WORKSPACE_STORAGE_KEY);
  if (!savedWorkspaceId) return null;
  return (
    workspaces.find(
      (workspace) => workspace.id === parseInt(savedWorkspaceId),
    ) ?? null
  );
}

function rememberWorkspace(workspace: Organization) {
  localStorage.setItem(WORKSPACE_STORAGE_KEY, workspace.id.toString());
}
